import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import UsageStatisticsSection from "../../organisms/charts/UsageStatisticsSection"
import ProgressBar from "../ProgressBar"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { MediaQuery, TERMINAL_MAX_WARNING, getTerminalGroupsText } from "../../../utils/design-helpers"
import { getPercentage, isEvenNumber, mbGbDisplay } from "../../../utils/helpers"
import TerminalStatusCircle from "../../atoms/TerminalStatusCircle"
import TextButton from "../../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../../atoms/icons/ArrowDropUpIcon"
import SharedDataPlanTerminalsOverview from "./SharedDataPlanTerminalOverview"
import { TerminalDataModel } from "../../../utils/models"
import BodyMedium from "../../atoms/typography/BodyMedium"
import Body from "../../atoms/typography/Body"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"

interface Props {
  terminals: Array<TerminalDataModel>
  sharedPlan: boolean
  apiPlanId: number
}

const SortingCol = styled(Col)`
  display: flex;
  justify-content: center;
`

const BarContentContainer = styled.div<{ greybg?: boolean }>`
  background-color: ${(props) => (props.greybg ? props.theme.colors.greyLighter : props.theme.colors.white)};
  padding: ${(props) => props.theme.spacings.large};

  :hover .poop {
    display: block;
  }
`

const ProductImage = styled.img`
  width: 35px;
  height: 35px;
  object-fit: cover;

  ${MediaQuery.XS} {
    width: 66px;
    height: 72px;
  }
`

const MobileTerminalContainer = styled.div<{ open?: boolean }>`
  padding: 1rem;
  background-color: ${(props) => (props.open ? props.theme.colors.greyLight : undefined)};
  border-radius: ${(props) => props.theme.borders.softRadius};
  margin: 4px;
`

const MobileTerminalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DataPlanTerminalsOverview: React.FunctionComponent<Props> = ({ terminals, sharedPlan, apiPlanId }) => {
  const { t } = useTranslation()
  const [terminalDetailsOpen, setTerminalDetailsOpen] = React.useState(false)

  const [mobileTerminalOpen, setMobileTerminalOpen] = React.useState(false)

  return (
    <div>
      {sharedPlan ? (
        <SharedDataPlanTerminalsOverview apiPlanId={apiPlanId} />
      ) : (
        <div>
          {terminals.map((terminal: TerminalDataModel, index: number) => (
            <div key={terminal.id}>
              <Hidden xs>
                <BarContentContainer greybg={isEvenNumber(index)}>
                  <Row align="center" onClick={() => setTerminalDetailsOpen(!terminalDetailsOpen)}>
                    <Col md={2}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ProductImage src={terminal.imgSrc} />
                        <Spacer spacing={0.5} />
                        <BodyMedium>{terminal.name}</BodyMedium>
                      </div>
                    </Col>
                    <SortingCol md={2}>
                      <BodyMedium textAlign="center">{terminal.serialNumber}</BodyMedium>
                    </SortingCol>
                    <SortingCol md={2}>
                      <BodyMedium textAlign="center">{getTerminalGroupsText(terminal?.groups ?? [])}</BodyMedium>
                    </SortingCol>
                    <SortingCol md={2}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TerminalStatusCircle status={terminal.status} />
                        <Spacer spacing={0.5} />
                        <BodyMedium>{terminal.status}</BodyMedium>
                      </div>
                    </SortingCol>
                    <SortingCol md={2}>
                      <div style={{ width: "100%" }}>
                        <ProgressBar
                          warningMax={TERMINAL_MAX_WARNING}
                          currentProgress={getPercentage(terminal.dataUsage?.used ?? 0, terminal.dataUsage?.total ?? 0)}
                          border
                        />
                        <BodyMedium textAlign="center">
                          {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
                        </BodyMedium>
                      </div>
                    </SortingCol>
                    <SortingCol md={2}>
                      <TextButton
                        title={t("details")}
                        icon={terminalDetailsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        onClick={() => setTerminalDetailsOpen(!terminalDetailsOpen)}
                      />
                    </SortingCol>
                  </Row>
                </BarContentContainer>
              </Hidden>

              <Visible xs>
                <MobileTerminalContainer
                  onClick={() => setMobileTerminalOpen(!mobileTerminalOpen)}
                  open={mobileTerminalOpen}
                >
                  <MobileTerminalRow>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ProductImage src={terminal.imgSrc} />
                      <Spacer spacing={0.5} />
                      <div>
                        <BodyMedium>{terminal.name}</BodyMedium>
                        <Body>{terminal.serialNumber}</Body>
                      </div>
                    </div>
                    <div>{mobileTerminalOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</div>
                  </MobileTerminalRow>
                  <SlideOutHeight isVisible={mobileTerminalOpen}>
                    <HorizontalSpacer spacing={4} />
                    <Body>Group:</Body>
                    <HorizontalSpacer spacing={1} />
                    <BodyMedium>{getTerminalGroupsText(terminal?.groups ?? [])}</BodyMedium>
                    <HorizontalSpacer spacing={4} />
                    <Body>Status:</Body>
                    <HorizontalSpacer spacing={1} />
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TerminalStatusCircle status={terminal.status} />
                      <Spacer spacing={0.5} />
                      <BodyMedium>{terminal.status}</BodyMedium>
                    </div>
                    <HorizontalSpacer spacing={4} />
                    <Body>Data usage:</Body>
                    <HorizontalSpacer spacing={1} />
                    <Row align="center">
                      <Col xs={6}>
                        <ProgressBar
                          warningMax={TERMINAL_MAX_WARNING}
                          currentProgress={getPercentage(terminal.dataUsage?.used ?? 0, terminal.dataUsage?.total ?? 0)}
                          border
                        />
                      </Col>
                      <Col xs={6}>
                        <Body textAlign="center">
                          {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
                        </Body>
                      </Col>
                    </Row>
                  </SlideOutHeight>
                </MobileTerminalContainer>
              </Visible>
              <UsageStatisticsSection
                isOpen={terminalDetailsOpen}
                id={terminal.id}
                name={terminal.name}
                detailsData={terminal}
              />
            </div>
          ))}
        </div>
      )}
      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default DataPlanTerminalsOverview
